import {httpClientV2} from "../../../../infrastructure/http";
import {
  BookingScheduleRepository,
  BookingTimeSlots,
  BookingTimeSlotsQuery,
  BookingTimeSlotsRepository,
  UnavailableDateRange,
} from "../../domain/types";
import {Weekday} from "../../domain/weekdays";
import {BookingAvailabilityMapperV2} from "../mappers/mapper.v2";
import {
  V2BookingDaysResponse,
  V2TimeSlotsResponse,
  V2UnavailableDatesResponse,
} from "../mappers/mapper.types";

/**
 * v2 API implementation of BookingScheduleRepository and BookingTimeSlotsRepository.
 *
 * Endpoints (all GET, all under the public booking namespace):
 *   public/booking/days?mentorId&serviceId
 *   public/booking/unavailable-dates?mentorId
 *   public/booking/time-slots?mentorId&serviceId&date&month&year&day&userTimeZone
 *
 * Pure TypeScript - no React imports.
 */
export class BookingAvailabilityRepositoryV2
  implements BookingScheduleRepository, BookingTimeSlotsRepository
{
  private readonly daysEndpoint = "public/booking/days";
  private readonly unavailableDatesEndpoint = "public/booking/unavailable-dates";
  private readonly timeSlotsEndpoint = "public/booking/time-slots";

  async getAvailableWeekdays(query: {
    mentorId: number;
    serviceId: number;
  }): Promise<Weekday[]> {
    const params = new URLSearchParams({
      mentorId: String(query.mentorId),
      serviceId: String(query.serviceId),
    });

    const body = await httpClientV2.get<V2BookingDaysResponse>(
      `${this.daysEndpoint}?${params.toString()}`
    );

    return BookingAvailabilityMapperV2.toAvailableWeekdays(body);
  }

  async getUnavailableDates(query: {mentorId: number}): Promise<UnavailableDateRange[]> {
    const params = new URLSearchParams({mentorId: String(query.mentorId)});

    const body = await httpClientV2.get<V2UnavailableDatesResponse>(
      `${this.unavailableDatesEndpoint}?${params.toString()}`
    );

    return BookingAvailabilityMapperV2.toUnavailableDates(body);
  }

  async getTimeSlots(query: BookingTimeSlotsQuery): Promise<BookingTimeSlots> {
    // v2 takes the query in the query string and resolves the coach's timezone
    // itself, so `mentorTimeZone` is deliberately not sent.
    const params = new URLSearchParams({
      mentorId: String(query.mentorId),
      serviceId: String(query.serviceId),
      date: String(query.date),
      month: String(query.month),
      year: String(query.year),
      day: query.day,
      userTimeZone: query.userTimeZone,
    });

    const body = await httpClientV2.get<V2TimeSlotsResponse>(
      `${this.timeSlotsEndpoint}?${params.toString()}`
    );

    return BookingAvailabilityMapperV2.toTimeSlots(body);
  }
}
